import IProduct from 'models/product';

export const DEFAULT_LIMIT = 20;

export const PARSE_IMAGE_BY: Record<'EAN' | 'ARTICLE', keyof IProduct> = {
  EAN: 'EAN',
  ARTICLE: 'purchasingDataSupplierArticleNo',
};

export const DB_NAME = 'bulk_import';
export const SAVED_FILE_ID = 'import_file';
export const SAVED_FILE_DATA_ID = 'import_file_data';
export const OBJECT_STORAGE_NAME = 'import_data';
export const SAVED_PRODUCT_IMAGE = 'image_file_data_list';
// The SheetPicker (sheet, header-row) pick. Persisted alongside the file/data
// so a navigate-away → back restore rebuilds the banner to match the imported
// slice — including a manual override the re-run analyzer can't recover.
export const SAVED_SHEET_SELECTION = 'import_sheet_selection';
// The file of the accepted AI import, so its review can still save the source file after a reload.
export const SAVED_AI_FILE_ID = 'import_ai_file';
export const LOCAL_STORE_ITEMS = {
  STEP: 'bulk_import.step',
  CATEGORY_TYPE_ID: 'bulk_import.categroy_type_id',
  PRODUCT_TYPE: 'bulk_import.product_type',
  PRODUCER_ID: 'bulk_import.producer_id',
  REGEX: 'bulk_import.regex',
  PARSE_IMAGE_BY: 'bulk_import.parse_image_by',
  IS_IGNORE_AI: 'bulk_import.is_ignore_ai',
  // The removed main-image choice; only deleted from browsers that still have it.
  OBSOLETE_IS_AI_MAIN: 'bulk_import.is_ai_main',
  IS_AI_IMPORT: 'bulk_import.is_ai_import',
  UPLOADED_PRODUCT_STATUS_SET: 'bulk_import.uploaded_product_status_set',
  LIMIT: 'bulk_import.limit',
  REMOVED_PRODUCT_INDEXS: 'bulk_import.removed_product_indexs',
  // The accepted AI import of this browser (utils/aiImportRun.ts).
  AI_IMPORT_RUN: 'bulk_import.ai_import_run',
  // The status each of them had when the bulk import page last showed it (utils/aiImportRun.ts).
  AI_IMPORT_SEEN: 'bulk_import.ai_import_seen',
  // The superuser's import followed on the import step, to come back to (utils/aiImportRun.ts).
  AI_IMPORT_FOLLOWED: 'bulk_import.ai_import_followed',
};

export const STEP_PATHS: Record<string, string> = {
  '1': 'select-category',
  '2': 'import-file',
  '3': 'field-mapping',
  '4': 'review',
};

export const ALLOWED_IMAGE_FILE_TYPE_LIST = ['image/jpeg', 'image/jpg', 'image/tiff', 'image/png', 'image/webp'];
export const ALLOWED_RECOGNIZE_IMAGE_FILE_TYPE_LIST = [...ALLOWED_IMAGE_FILE_TYPE_LIST, 'application/pdf'];

export const QUESTION_FIELDS: (keyof IProduct)[] = [
  'reachSubstance',
  'bestBeforeDate',
  'childrenUnder16',
  'reachSubstancesAnnex19072006',
  'regulatedSubstance',
  'hazardousSubstancesOver1',
  'informationHazardousSubstances',
  'checkUniqueIdentification',
  'explanationNoUDI',
  'cmrSubstance',
  'CMRSubstances',
  'approvalDietFeed',
];

export const BOOLEAN_CHOICE_FIELDS: (keyof IProduct)[] = [
  'qrLabelingSwedish',
  'qrMedicalClaims',
  'qrMarketingClaimsCosmetic',
  'qrEfsaUlValues',
  'qrFoodHygiene',
  'qrFoodLabeling',
  'qrMisleadingSalesTerms',
  'qrClassificationLabelingPackaging',
  'qrAdditivesInFeed',
  'qrFeedLabeling',
  'qrRelevantFeedCertifications',
  'complianceEg12232009',
  'complianceReach19072006',
  'complianceProductSafety2004451',
  'complianceMedicalTechProducts',
  'complianceHaccp',
  'containsBannedSubstancesPops',
  'reachSubstance',
  'childrenUnder16',
  'reachSubstancesAnnex19072006',
  'hazardousSubstancesOver1',
  'cmrSubstance',
  'tumbleDrying',
  'dryCleaning',
  'chlorineBleaching',
  'batteryRechargeable',
  'lampDimmable',
  'checkUniqueIdentification',
  'ceMarking',
  'plasticTrayPackaging',
  'shrinkWrapPackaging',
  'bestBeforeDate',
  'compliancePpwrPackaging',
  'compliancePops',
];

export const ENUM_SELECT_FIELDS: (keyof IProduct)[] = [
  'containsBattery',
  'complianceBiocid',
  'complianceBiocidalTreated',
  'confirmGuidanceSwedishElectronics',
  'qrNutritionClaimsCode',
  'qrHealthClaimsCode',
  'qrElectronicCode',
  'complianceToyDirective',
  'complianceMetalsSkinContact',
  'approvalDietFeed',
  'dagsdosUnit',
  'flammableSubtype',
  'eggWeightClass',
  'classification',
  'recyclingType',
  'animalFeedType',
  'fishProductionMethod',
  'fishCatchMethod',
  'catchArea',
  'nutritional_unit',
  'ironing',
  'batteryType',
  'batteryTechnology',
  'lampEnergyClass',
  'descriptive_size_unit',
  'preparedForm',
  'signalWords',
  'categoryWeightloss',
  'complianceSunscreenRegulation',
  'sunProtectionFactor',
  'alcoholPercentage',
  'recycling_amount',
  'basicDataTaxClassification',
  'country_of_manufacturing_string',
];

export const ENUM_MULTIPLE_SELECT_FIELDS: (keyof IProduct)[] = [
  'hazardSymbols',
  'animalCategoriesForFeed',
  'batteryHazardousMetals',
  'additionalCharacteristics',
];

export const ENUM_MULTIPLE_GROUPED_SELECT_FIELDS: (keyof IProduct)[] = [
  'labels',
  'hazardStatements',
  'securityNotices',
];

// Product-form input rules: FloatInput takes non-negative decimals, IntegerField whole numbers.
export const FLOAT_FIELDS: (keyof IProduct)[] = [
  'vitaminA',
  'vitaminD',
  'vitaminE',
  'vitaminK',
  'vitaminC',
  'tiamin',
  'riboflavin',
  'niacin',
  'vitaminB6',
  'folsyra',
  'vitaminB12',
  'biotin',
  'pantotensyra',
  'kalium',
  'klorid',
  'kalcium',
  'fosfor',
  'magnesium',
  'jarn',
  'zink',
  'koppar',
  'mangan',
  'fluorid',
  'selen',
  'krom',
  'molybden',
  'jod',
  'polyoler',
  'starkelse',
  'yarnWeight',
  'yarnRunLength',
  'knittingGaugeStitches',
  'knittingGaugeRows',
  'crochetGaugeStitches',
  'crochetGaugeRows',
];

export const INTEGER_FIELDS: (keyof IProduct)[] = ['washingTemperature'];

export const NUMBER_RULE_FIELDS: (keyof IProduct)[] = ['alcoholPercentage', ...FLOAT_FIELDS, ...INTEGER_FIELDS];

export const NUMBER_VALUE_FIELDS: (keyof IProduct)[] = [
  'recycling_amount',
  'basicDataTaxClassification',
  ...NUMBER_RULE_FIELDS,
];

// FC-2021: legacy Boolean answers replaced by nullable enum codes. The server keeps
// both in sync, so the bulk flow works with the code only and drops the Boolean.
export const LEGACY_QUALITY_CODE_FIELDS: Partial<Record<keyof IProduct, keyof IProduct>> = {
  qrNutritionClaims: 'qrNutritionClaimsCode',
  qrHealthClaims: 'qrHealthClaimsCode',
  qrElectronic: 'qrElectronicCode',
};

// CLP phrase slot fills, keyed by the code list they belong to.
export const PHRASE_FILL_CODE_FIELDS: Record<'hazardStatementFills' | 'securityNoticeFills', keyof IProduct> = {
  hazardStatementFills: 'hazardStatements',
  securityNoticeFills: 'securityNotices',
};
export const PHRASE_FILL_FIELDS = Object.keys(PHRASE_FILL_CODE_FIELDS) as (keyof typeof PHRASE_FILL_CODE_FIELDS)[];

export const NULLABLE_FIELDS = [
  ...BOOLEAN_CHOICE_FIELDS,
  ...ENUM_SELECT_FIELDS,
  ...ENUM_MULTIPLE_SELECT_FIELDS,
];

export const MULTIPLE_TEXT_FIELDS: (keyof IProduct)[] = [
  'short_text',
  'shortSalesDescription',
  'search_terms',
  'ingredient_statement',
  'animalFoodIngredients',
  'feedAdditives',
  'feedingInstructions',
  'analyticalConstituentsFeed',
  'non_food_ingredients',
  'trade_item_temperature_information',
  'preparationInstructions',
  'dosageAndUsageInstructions',
  'wasteManagementAndRecyclingInformation',
  'place_of_item_activity',
  'consumerContactAddress',
];

// excel
export const EXCEL_ERROR_VALUES = ['#N/A', '#REF!', '#NAME?', '#DIV/0!', '#NULL!', '#VALUE!', '#NUM!'];

export const HEADER_MAPPING_STATUS = {
  NO_MAPPING: 'no',
  MAPPING_SUCCESS: 'success',
  MAPPING_FAILED: 'failed',
};

export const STATUS_COLORS = {
  ERROR: '#F95E5D',
  WARNING: '#FFFF00',
  SUCCESS: '#9DC683',
  QUESTION_SUCCESS: '#9DD1DA',
  NORMAL: '#FFFFFF',
};

export const STATUS_COLORS_OPACITY = {
  ERROR: '#FFEFEE',
  WARNING: '#FFFFEC',
  SUCCESS: '#F5F9F2',
  QUESTION_SUCCESS: '#9DD1DA',
  NORMAL: '#FFFFFF',
};

export const VALID_DATE_FORMATS = [
  'dd.MM.yy',
  'dd.MM.yyyy',
  'dd/MM/yy',
  'dd/MM/yyyy',
  'dd-MM-yy',
  'dd-MM-yyyy',
  'dd MMM yy',
  'dd MMM yyyy',
  'dd MMM, yy',
  'dd MMM, yyyy',
  'yyyy-MM-dd',
];
